// import AccountSelector from "components/AccountSelect"
// import PurchasePlanSelector from "components/PurchasePlanSelect"

import { changeModalState, cleanEvenType } from "reducers/event-types/event-types-actions"
import { useAppDispatch, useAppSelector } from "reducers/store"

import ApiEventTypes from "api/ApiEventTypes"
import clsx from "clsx"
import CurrencySelect from "components/CurrencySelect"
import ProductSelector from "components/EvtKit/ProductSelector"
import Button from "components/EvtKit/button/Button"
import Input from "components/EvtKit/input/Input"
import CustomModalComponent from "components/EvtKit/modal/CustomModalComponent"
import Switch from "components/EvtKit/switch/Switch"
import Tooltip from "components/Tooltip"
import Heading from "components/Typography/Heading"
import { useCallback, useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import { processFormatValue, processInitialFormatValue } from "utils/formatPrice"
import { useMutation, useQueryClient } from "react-query"
import { Colors } from "themes/palette"
import { FIRST_STEPS } from "reducers/accounts/actions-types"
import useHandleApiError from "hooks/useHandleApiErrors"

const ButtonsContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  margin-top: 12px;
  width: 100%;
  gap: 36px;
`
const ContainerColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: normal;
  justify-content: center;
  margin: 0 auto;
  gap: 24px;
  justify-content: space-between;
`
const ContainerGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
`
const SwitchGrid = styled.div`
  display: flex;
`

const NameHead = styled.p`
  color: ${(props) => props.theme.palette.primary_one};
  font-size: 16px;
  line-height: 19px;
  padding-bottom: 16px;
  font-weight: bold;
`

const SelectContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`
const SwitchButton = styled.button`
  height: 24px;
  width: 45px;
`
const SwitchContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 200px;
`
const SwitchLabel = styled.p`
  color: ${(props) => props.theme.palette.primary_one};
  font-size: 16px;
`

const ProductContainer = styled.div`
  justify-content: center;
  width: 100%;
`
const FieldSet = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 48px;
  margin: 24px 0px;
`
const SwitchFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`
const ContainerFreeTrial = styled.div`
  width: 100%;
`

const ContainerFreeTrialInputs = styled.div`
  display: flex;
  gap: 24px;
`

const ContainerHeading = styled.div`
  margin-bottom: 32px;
`
const ButtonCustom = styled(Button)`
  width: 100%;
`

export const EventModal = () => {
  const appDispatch = useAppDispatch()

  const queryClient = useQueryClient()

  const { state, type } = useAppSelector((state) => state.eventTypes.modal)
  const {
    key,
    name,
    price,
    hasFreeTrial,
    product,
    isSimplePricing,
    currencyType,
    account,
    freeTrialDuration,
    purchasePlan
  } = useAppSelector((state) => state.eventTypes.event)

  const [freeTrial, setFreeTrial] = useState(hasFreeTrial)
  const [inputData, setInputData] = useState({
    inputKey: key,
    inputName: name,
    inputPrice: price ? processInitialFormatValue(price) : "0,00",
    checkSimplePricing: isSimplePricing,
    selectAccounts: account,
    selectPurchasePlan: purchasePlan,
    selectCurrencyType: currencyType,
    inputFreeTrialDuration: freeTrialDuration
  })

  const [productSelector, setProductSelector] = useState("")
  const [isNumeric, setIsNumeric] = useState(false)

  const [isSwitchButtonClicked, setSwitchButtonClicked] = useState<boolean>()

  const handleModalState = useCallback(() => {
    appDispatch(changeModalState(false, ""))
    appDispatch(cleanEvenType())
    setInputData({
      inputKey: "",
      inputName: "",
      inputPrice: "",
      checkSimplePricing: false,
      selectAccounts: "",
      selectPurchasePlan: "",
      selectCurrencyType: "",
      inputFreeTrialDuration: {
        usageLimit: 0
      }
    })
    setProductSelector("")
    setFreeTrial(false)
    setSwitchButtonClicked(false)
  }, [])

  const handleSuccess = useCallback(() => {
    handleModalState()

    queryClient.invalidateQueries(FIRST_STEPS)

    window.location.reload()
  }, [handleModalState])

  const { getErrorMessageAndShowErrorToast } = useHandleApiError()

  const updateEventType = useMutation({
    mutationFn: async (data: App.EventTypesRequest) => {
      await new ApiEventTypes().updateByKey(data)
    },
    onSuccess: handleSuccess,
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, "Ocorreu algum erro. Verifique os dados preenchidos e tente novamente.")
    }
  })

  const createEventType = useMutation({
    mutationFn: async (data: App.EventTypesRequest) => {
      await new ApiEventTypes().create(data)
    },
    onSuccess: handleSuccess,
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, "Ocorreu algum erro. Verifique os dados preenchidos e tente novamente.")
    }
  })

  const handleSubmit = useCallback(async () => {
    const rawPrice = inputData.inputPrice ? Number(inputData.inputPrice.replaceAll(",", ".")) : 0

    const eventType = {
      key: inputData.inputKey || key,
      name: inputData.inputName || name,
      price: rawPrice || price,
      hasFreeTrial: freeTrial,
      product: product || productSelector,
      account: inputData.selectAccounts || account,
      purchasePlan: inputData.selectPurchasePlan || purchasePlan,
      isSimplePricing: inputData.checkSimplePricing,
      currencyType: inputData.selectCurrencyType || currencyType,
      freeTrialDuration: inputData.inputFreeTrialDuration || freeTrialDuration
    }

    if (type === "update") {
      updateEventType.mutate(eventType)
    }

    if (type === "create") {
      createEventType.mutate(eventType)
    }
  }, [
    inputData,
    key,
    name,
    price,
    freeTrial,
    product,
    productSelector,
    type,
    account,
    isSimplePricing,
    currencyType,
    freeTrialDuration,
    purchasePlan
  ])

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target

      setInputData((prevState) => ({
        ...prevState,
        [name]: name === "inputPrice" ? processFormatValue(value) : value
      }))
    },
    [inputData.selectCurrencyType]
  )
  const handleFreeTrialUsage = useCallback(
    (e) => {
      const { value } = e.target
      const hasNonNumericCharacters = /\D/.test(value)

      setIsNumeric(hasNonNumericCharacters)
      setInputData((prevState) => ({
        ...prevState,
        inputFreeTrialDuration: {
          usageLimit: value
        }
      }))
    },
    [setInputData, setIsNumeric]
  )

  const handleSelectChange = useCallback(
    (productValue) => {
      setProductSelector(productValue)
    },
    [setProductSelector]
  )

  const currencyBySelectedCurrencyType = useMemo(() => {
    if (!inputData.selectCurrencyType) return ""

    return inputData.selectCurrencyType === "BRL" ? "R$ " : "US$ "
  }, [inputData.selectCurrencyType])

  //Information that will be used in the future

  // const handleAccountSelectChange = useCallback(
  //   (accountValue) => {
  //     setInputData((prevState) => {
  //       const newState = {
  //         ...prevState,
  //         selectAccounts: accountValue
  //       }

  //       return newState
  //     })
  //   },
  //   [setInputData]
  // )

  // const handlePurchasePlanSelectChange = useCallback(
  //   (purchasePlanValue) => {
  //     setInputData((prevState) => ({
  //       ...prevState,
  //       selectPurchasePlan: purchasePlanValue
  //     }))
  //   },
  //   [setInputData]
  // )

  const handleFreeTrial = useCallback(() => {
    setFreeTrial((prev) => !prev)
    setSwitchButtonClicked((prev) => !prev)

    if (!isSwitchButtonClicked) {
      setInputData((prev) => ({
        ...prev,
        inputFreeTrialDuration: {
          usageLimit: 0
        }
      }))
    }
  }, [setFreeTrial, setSwitchButtonClicked])

  useEffect(() => {
    if (type === "update") {
      setInputData({
        inputKey: key,
        inputName: name,
        inputPrice: processInitialFormatValue(price),
        checkSimplePricing: isSimplePricing,
        selectAccounts: account,
        selectPurchasePlan: purchasePlan,
        selectCurrencyType: currencyType,
        inputFreeTrialDuration: freeTrialDuration
      })
      setFreeTrial(hasFreeTrial || !!freeTrialDuration.usageLimit)
      setSwitchButtonClicked(hasFreeTrial)
    }
  }, [type])

  const isDisabled = useMemo(() => {
    const requiredFields = ["inputKey", "inputName", "inputPrice", "selectCurrencyType"]

    const isValidForm = requiredFields.every((key) => inputData[key]?.length > 0)

    const hasValidFreeTrial =
      (!!isSwitchButtonClicked && (inputData.inputFreeTrialDuration?.usageLimit ?? 0) > 0) || !isSwitchButtonClicked
    const hasProduct = !!product || !!productSelector

    return !hasProduct || !isValidForm || !hasValidFreeTrial
  }, [inputData, isSwitchButtonClicked, updateEventType.isLoading, createEventType.isLoading, productSelector])

  return (
    <CustomModalComponent
      className="modal-user without-margin"
      openModal={state && (type === "update" || type === "create")}
      closeModal={handleModalState}
      size="3-lg"
      type={type}
      handleSubmit={handleSubmit}
      createModalTitle="Criar Tipo de Evento"
      updateModalTitle="Editar Tipo de Evento"
    >
      <ContainerHeading>
        <NameHead>{name}</NameHead>
        <Heading variant="h6" color="gray-dark">
          Para criar um Tipo de Evento, preencha os campos abaixo, fornecendo as informações necessárias.
        </Heading>
      </ContainerHeading>
      <ContainerColumn>
        <ContainerGrid>
          <Tooltip text=" Identificador único e imutável">
            <Input
              type="text"
              name="inputKey"
              label="Chave Identificadora"
              disabled={type === "update"}
              className="fullWidth"
              onChange={handleChange}
              placeholder="Digite"
              shrink
              value={inputData.inputKey}
            />
          </Tooltip>

          <SelectContainer>
            <ProductContainer>
              <ProductSelector
                onChange={handleSelectChange}
                isDisabled={type === "update"}
                disabledValue={product}
                value={productSelector}
                label="Produto"
                className="fullWidth"
              />
            </ProductContainer>
          </SelectContainer>
        </ContainerGrid>
        <FieldSet>
          <Tooltip text="Digite o nome do evento de cobrança aqui.">
            <Input
              type="text"
              name="inputName"
              label="Nome"
              shrink
              value={inputData.inputName}
              placeholder="Digite"
              onChange={handleChange}
              className="fullWidth"
            />
          </Tooltip>
          <SelectContainer>
            <ProductContainer>
              <CurrencySelect value={inputData.selectCurrencyType} setInputData={setInputData} label="Moeda" />
            </ProductContainer>
          </SelectContainer>
          <Tooltip text="Insira o valor de uso.">
            <Input
              type="text"
              name="inputPrice"
              label="Preço (Somente números)"
              shrink
              value={`${currencyBySelectedCurrencyType}${inputData.inputPrice}`}
              onChange={handleChange}
              className="fullWidth"
              placeholder="Digite"
            />
          </Tooltip>
        </FieldSet>

        <SwitchFields>
          <Heading variant="h6" color="gray-dark">
            Ao ativar o Free Trial, o usuário recebe 14 dias gratuitos para utilizar o evento limitado a “quantidade de
            utilização” definida.
          </Heading>
          <SwitchGrid>
            <SwitchContainer>
              <SwitchButton type="button" onClick={handleFreeTrial}>
                <Switch initialState={hasFreeTrial} value={freeTrial} />
              </SwitchButton>
              <SwitchLabel>Free Trial (Gratuito)</SwitchLabel>
            </SwitchContainer>
          </SwitchGrid>
          <ContainerFreeTrialInputs>
            <ContainerFreeTrial>
              <Input
                type="text"
                name="freeTrialDuration"
                label="Duração do Free Trial"
                shrink
                value="14 dias"
                className="fullWidth"
                disabled
                informativeMessage="A duração do Free Trial não pode ser alterada."
                colorIcon={Colors.informative}
                color="informative"
              />
            </ContainerFreeTrial>
            <ContainerFreeTrial>
              <Tooltip text="Insira a quantidade utilização de FreeTrial.">
                <Input
                  type="text"
                  name="inputFreeTrialDuration"
                  label="Quantidade de utilização do evento"
                  shrink
                  value={inputData.inputFreeTrialDuration.usageLimit || ""}
                  onChange={handleFreeTrialUsage}
                  error={isNumeric}
                  className="fullWidth"
                  disabled={!isSwitchButtonClicked}
                  placeholder="Digite"
                />
              </Tooltip>
            </ContainerFreeTrial>
          </ContainerFreeTrialInputs>
        </SwitchFields>
        <ContainerGrid>
          {/* Information that will be used in the future
          <SelectContainer>
                    <ProductContainer>
                      <PurchasePlanSelector
                        onChange={handlePurchasePlanSelectChange}
                        value={inputData.selectPurchasePlan}
                        product={productSelector || product}
                      />
                    </ProductContainer>
                  </SelectContainer> */}
          {/* <SelectContainer>
                    <ProductContainer>
                      <AccountSelector
                        onChange={handleAccountSelectChange}
                        value={inputData.selectAccounts}
                        product={productSelector || product}
                      />
                    </ProductContainer>
                  </SelectContainer> */}
        </ContainerGrid>
      </ContainerColumn>
      <ButtonsContainer>
        <ButtonCustom onClick={handleModalState} label="Cancelar" className={clsx("secondary")} />
        <ButtonCustom
          type="submit"
          label="Confirmar"
          className={clsx("background-gradient", { isDisabled })}
          onClick={handleSubmit}
          disabled={isDisabled}
        />
      </ButtonsContainer>
    </CustomModalComponent>
  )
}
